import {
  fetchStack,
  getStackType,
  selectEnvironmentsByStackId,
  selectIsAccountOwner,
  selectStackById,
} from "@app/deploy";
import { createSupportTicket, resetSupportTicket } from "@app/deploy/support";
import { selectOrganizationSelected } from "@app/organizations";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import { escapeHtml } from "@app/string-utils";
import { selectCurrentUser } from "@app/users";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import {
  Banner,
  BannerMessages,
  Box,
  Button,
  CheckBox,
  Form,
  FormGroup,
  IconAlertTriangle,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
  ModalWrapper,
  TextArea,
  Tooltip,
  tokens,
} from "../shared";

const OTHER_REASON = "Other";

const DEPROVISION_REASONS = [
  "No longer needed",
  "Shutting down account",
  "Migrating elsewhere",
  OTHER_REASON,
];

const StackDestroy = ({ stackId }: { stackId: string }) => {
  useQuery(fetchStack({ id: stackId }));

  const dispatch = useDispatch();
  const org = useSelector(selectOrganizationSelected);
  const currentUser = useSelector(selectCurrentUser);
  const loader = useLoader(createSupportTicket);

  const isAccountOwner = useSelector((s) =>
    selectIsAccountOwner(s, { orgId: org.id }),
  );

  // Reset loader state on unmount so success state does not persist across pages
  useEffect(() => {
    return () => {
      dispatch(resetSupportTicket());
    };
  }, []);

  const stack = useSelector((s) => selectStackById(s, { id: stackId }));
  const environments = useSelector((s) =>
    selectEnvironmentsByStackId(s, { stackId }),
  );
  const stackType = getStackType(stack);

  const [isOpen, setIsOpen] = useState(false);
  const [reasons, setReasons] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [confirm, setConfirm] = useState("");

  const resetForm = () => {
    setReasons([]);
    setMessage("");
    setConfirm("");
  };

  const openModal = () => {
    resetForm();
    // Clear any banner from a previous request so the card starts clean.
    dispatch(resetSupportTicket());
    setIsOpen(true);
  };

  const closeModal = () => setIsOpen(false);

  useLoaderSuccess(loader, () => {
    resetForm();
    closeModal();
  });

  const toggleReason = (reason: string) => {
    setReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason],
    );
  };

  // If it's a shared stack, show different UI
  if (stackType === "shared") {
    return (
      <Box>
        <h1 className="text-lg text-gray-500 font-semibold flex items-center gap-2 mb-4">
          <IconAlertTriangle color="#6B7280" />
          Deprovision Stack
        </h1>
        <Banner variant="info">
          Shared Stacks cannot be deprovisioned. Note: Shared Stacks have no
          additional costs.
        </Banner>
      </Box>
    );
  }

  // Environments must be torn down first; the API reports per-resource errors
  // when an individual environment is deleted.
  const canDeprovision = environments.length === 0;

  /** Why the request cannot be started yet, or null when it can. */
  const getBlockedReason = () => {
    if (!isAccountOwner) {
      return "You must be an Account Owner to deprovision stacks";
    }
    if (!canDeprovision) {
      return "You must delete all active resources and environments within this stack before proceeding";
    }
    return null;
  };
  const blockedReason = getBlockedReason();

  const messageRequired = reasons.includes(OTHER_REASON);

  const invalid =
    reasons.length === 0 ||
    confirm !== stack.name ||
    (messageRequired && !message.trim());

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (invalid || blockedReason !== null || loader.isLoading) return;

    const subject = "Stack Deprovision Request";
    const stackName = escapeHtml(stack.name);
    let description = `<p>Your request has been received by the Aptible Support team. If you'd like to cancel this request, reply here to let us know.</p>`;
    description += "<p>--</p>";
    description += `<p><strong>User requested stack deprovision for:</strong> ${stackName} (ID: ${stack.id})</p>`;
    description += `<p><strong>User:</strong> ${escapeHtml(currentUser.name)} (${escapeHtml(currentUser.email)})<br/>`;
    description += `<strong>Organization:</strong> ${escapeHtml(org.name)} (ID: ${org.id})<br/>`;
    description += `<strong>Stack ID:</strong> ${stack.id}<br/>`;
    description += `<strong>Stack Name:</strong> ${stackName}</p>`;

    // Listed in the order shown in the form, not the order they were clicked,
    // so the ticket reads consistently.
    const orderedReasons = DEPROVISION_REASONS.filter((option) =>
      reasons.includes(option),
    );
    description += `<p><strong>Reasons for deprovisioning:</strong><br/>${orderedReasons.join("<br/>")}</p>`;
    if (message.trim()) {
      // Escape before newline conversion so the <br/> tags survive.
      description += `<p><strong>Additional details:</strong><br/>${escapeHtml(
        message.trim(),
      ).replace(/\n/g, "<br/>")}</p>`;
    }
    description +=
      "<p><strong>Status:</strong> No billable resources are currently running in environments.</p>";

    const action = createSupportTicket({
      subject,
      description,
      email: currentUser.email,
      name: currentUser.name,
      priority: "normal",
      attachments: [],
    });

    dispatch(action);
  };

  const requestButton = (
    <Button
      type="button"
      variant="delete"
      onClick={openModal}
      disabled={blockedReason !== null}
      className="shrink-0"
    >
      Request Deprovision of Stack
    </Button>
  );

  return (
    <>
      <Box>
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Deprovision Stack
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Permanently deprovision this stack and all of its environments.
            </p>
          </div>
          {blockedReason === null ? (
            requestButton
          ) : (
            <Tooltip
              text={blockedReason}
              referenceClassName="shrink-0 cursor-not-allowed"
            >
              {requestButton}
            </Tooltip>
          )}
        </div>

        {/* On the card, not in the modal: the modal unmounts on success, so a
            banner inside it would never be seen. */}
        <BannerMessages className="mt-4" {...loader} />
      </Box>

      {isOpen ? (
        <ModalWrapper>
          <Modal isOpen={isOpen} onClose={closeModal} className="min-w-[550px]">
            <ModalHeading title="Deprovision stack" onClose={closeModal} />
            <Form onSubmit={onSubmit}>
              <ModalBody>
                <div className="flex flex-col gap-5">
                  <p className="text-gray-700">
                    Your stack <strong>{stack.name}</strong> and all of its
                    environments will be permanently deprovisioned once this
                    process is complete. This cannot be undone.
                  </p>

                  <div
                    className="flex flex-col gap-1"
                    role="group"
                    aria-labelledby="deprovision-reasons-label"
                  >
                    <div
                      className={tokens.type.h4}
                      id="deprovision-reasons-label"
                    >
                      Reasons for deprovisioning{" "}
                      <span className="text-red-500">*</span>
                      <span className="ml-1 font-normal text-gray-500">
                        &mdash; select all that apply
                      </span>
                    </div>
                    <div className="flex flex-col gap-2 mt-1">
                      {DEPROVISION_REASONS.map((option) => (
                        <CheckBox
                          key={option}
                          label={option}
                          labelClassName="ml-3"
                          checked={reasons.includes(option)}
                          onChange={() => toggleReason(option)}
                        />
                      ))}
                    </div>
                  </div>

                  <FormGroup
                    label={
                      <>
                        Tell us more{" "}
                        {messageRequired ? (
                          <span className="text-red-500">*</span>
                        ) : null}
                      </>
                    }
                    htmlFor="deprovision-message"
                  >
                    <TextArea
                      id="deprovision-message"
                      name="deprovision-message"
                      placeholder="Please share more detail about why you're deprovisioning this stack"
                      value={message}
                      onChange={(e) => setMessage(e.currentTarget.value)}
                    />
                  </FormGroup>

                  <FormGroup
                    label={
                      <>
                        Type "{stack.name}" to confirm{" "}
                        <span className="text-red-500">*</span>
                      </>
                    }
                    htmlFor="delete-confirm"
                  >
                    <Input
                      id="delete-confirm"
                      name="delete-confirm"
                      type="text"
                      value={confirm}
                      onChange={(e) => setConfirm(e.currentTarget.value)}
                      disabled={loader.isLoading}
                    />
                  </FormGroup>

                  {/* Failures keep the modal open, so they are reported here
                      rather than on the card behind it. */}
                  {loader.isError ? <BannerMessages {...loader} /> : null}
                </div>
              </ModalBody>
              <ModalFooter
                actions={[
                  <Button
                    key="cancel"
                    type="button"
                    variant="white"
                    onClick={closeModal}
                  >
                    Cancel
                  </Button>,
                  <Button
                    key="submit"
                    type="submit"
                    variant="delete"
                    disabled={invalid || loader.isLoading}
                    isLoading={loader.isLoading}
                  >
                    Submit Deprovision Request
                  </Button>,
                ]}
              />
            </Form>
          </Modal>
        </ModalWrapper>
      ) : null}
    </>
  );
};

export const StackDetailSettingsPage = () => {
  const { id = "" } = useParams();

  return (
    <div className="flex flex-col gap-4">
      <StackDestroy stackId={id} />
    </div>
  );
};
